import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomValidators } from '@shared/customValidators';
import { collectFormErrors } from '@shared/formHelpers';
import { BusinessActivitySurvey } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'company-description',
  templateUrl: './company-description.component.html',
  styles: ``
})
export class CompanyDescriptionComponent implements OnInit {
    private readonly subStepName = "Company Description";
    private businessActivitySurveyToSave: BusinessActivitySurvey;
    loading: boolean = false;
    form: UntypedFormGroup;
    isEligibleForStartupOffer: boolean;
    companyDescriptionMaxlength: number;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const businessActivitySurvey: BusinessActivitySurvey = this.route.snapshot.data.businessActivitySurvey;
        this.businessActivitySurveyToSave = {
            ...businessActivitySurvey
        };
        this.isEligibleForStartupOffer = this.registrationService.isEligibleForStartupOffer();
        this.companyDescriptionMaxlength = CustomValidators.getBusinessActivitySurveyMaxlength("businessDescription");
        this.form = this.fb.group({
            description: [businessActivitySurvey?.businessDescription, [
                Validators.required,
                Validators.minLength(50),
                Validators.maxLength(this.companyDescriptionMaxlength)
            ]]
        });
    }

    private isValid(): boolean {
        this.form.markAllAsTouched();
        return this.form.valid;
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;
            this.businessActivitySurveyToSave.businessDescription = this.form.value.description;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            try {
                await this.registrationService.saveBusinessActivitySurvey(this.businessActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.BUSINESS_DESCRIPTION);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save business activity survey failed', e, 'with business activity survey', this.businessActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

}
